import { useState, useMemo } from 'react';
import SEO from '../../components/SEO';
import AdUnit from '../../components/AdUnit';

var OLD_REGIME_SLABS = [
  { min: 0, max: 250000, rate: 0 },
  { min: 250000, max: 500000, rate: 5 },
  { min: 500000, max: 1000000, rate: 20 },
  { min: 1000000, max: Infinity, rate: 30 },
];

var NEW_REGIME_SLABS = [
  { min: 0, max: 400000, rate: 0 },
  { min: 400000, max: 800000, rate: 5 },
  { min: 800000, max: 1200000, rate: 10 },
  { min: 1200000, max: 1600000, rate: 15 },
  { min: 1600000, max: 2000000, rate: 20 },
  { min: 2000000, max: 2400000, rate: 25 },
  { min: 2400000, max: Infinity, rate: 30 },
];

function calcSlabTax(income, slabs) {
  var tax = 0;
  for (var i = 0; i < slabs.length; i++) {
    var s = slabs[i];
    if (income <= s.min) break;
    var taxable = Math.min(income, s.max) - s.min;
    tax += taxable * (s.rate / 100);
  }
  return tax;
}

function SalaryCalculator() {
  var [salary, setSalary] = useState(1200000);
  var [payFreq, setPayFreq] = useState('annual');
  var [regime, setRegime] = useState('new');
  var [hra, setHra] = useState(0);
  var [section80c, setSection80c] = useState(150000);
  var [section80d, setSection80d] = useState(25000);
  var [otherDeductions, setOtherDeductions] = useState(0);
  var [epf, setEpf] = useState(21600);

  var result = useMemo(function () {
    var annual = payFreq === 'annual' ? salary : payFreq === 'monthly' ? salary * 12 : payFreq === 'biweekly' ? salary * 26 : salary * 52;

    // Old regime: standard deduction 50k + all deductions
    var oldDeductions = 50000 + Math.min(section80c, 150000) + Math.min(section80d, 100000) + hra + epf + otherDeductions;
    var oldTaxable = Math.max(0, annual - oldDeductions);
    var oldTax = calcSlabTax(oldTaxable, OLD_REGIME_SLABS);
    // Rebate u/s 87A for old regime (income up to 5L)
    if (oldTaxable <= 500000) oldTax = 0;
    var oldCess = oldTax * 0.04;
    var oldTotal = oldTax + oldCess;

    // New regime: standard deduction 75k, no other deductions
    var newTaxable = Math.max(0, annual - 75000);
    var newTax = calcSlabTax(newTaxable, NEW_REGIME_SLABS);
    // Rebate u/s 87A for new regime (income up to 12L)
    if (newTaxable <= 1200000) newTax = 0;
    var newCess = newTax * 0.04;
    var newTotal = newTax + newCess;

    var tax = regime === 'new' ? newTotal : oldTotal;
    var net = annual - tax;

    return {
      grossAnnual: annual,
      grossMonthly: annual / 12,
      grossBiweekly: annual / 26,
      grossWeekly: annual / 52,
      grossDaily: annual / 260,
      grossHourly: annual / 2080,
      oldTax: oldTotal,
      newTax: newTotal,
      tax: tax,
      netAnnual: net,
      netMonthly: net / 12,
      netDaily: net / 260,
      effectiveRate: annual > 0 ? ((tax / annual) * 100).toFixed(1) : '0.0',
      savings: regime === 'new' ? oldTotal - newTotal : newTotal - oldTotal,
    };
  }, [salary, payFreq, regime, hra, section80c, section80d, otherDeductions, epf]);

  var fmt = function (n) {
    var abs = Math.abs(Math.round(n));
    var sign = n < 0 ? '-' : '';
    if (abs >= 10000000) return sign + '₹' + (abs / 10000000).toFixed(2) + ' Cr';
    if (abs >= 100000) return sign + '₹' + (abs / 100000).toFixed(2) + ' L';
    return sign + '₹' + abs.toLocaleString('en-IN');
  };

  return (
    <div className="page-prettifier">
      <SEO title="Indian Salary & Tax Calculator - FY 2025-26 | Old vs New Regime" description="Free Indian salary calculator with FY 2025-26 tax slabs. Compare old vs new tax regime, calculate take-home pay with HRA, 80C, 80D deductions." path="/salary-calculator" />
      <div className="container">
        <div className="tool-header">
          <h1>Indian Salary & Tax Calculator (FY 2025-26)</h1>
          <p>Calculate your take-home pay with the latest Indian tax slabs. Compare old vs new regime and optimize your deductions.</p>
        </div>
        <AdUnit />
        <div className="calc-layout">
          <div className="calc-inputs">
            <div className="form-group">
              <label htmlFor="sal-amount">Salary Amount (₹)</label>
              <input id="sal-amount" type="number" min="0" value={salary} onChange={function (e) { setSalary(parseFloat(e.target.value) || 0); }} className="color-input" />
            </div>
            <div className="form-group">
              <label htmlFor="sal-freq">Pay Frequency</label>
              <select id="sal-freq" value={payFreq} onChange={function (e) { setPayFreq(e.target.value); }} className="indent-select" style={{ width: '100%' }}>
                <option value="annual">Annual (CTC)</option>
                <option value="monthly">Monthly</option>
                <option value="biweekly">Biweekly</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="sal-regime">Tax Regime</label>
              <select id="sal-regime" value={regime} onChange={function (e) { setRegime(e.target.value); }} className="indent-select" style={{ width: '100%' }}>
                <option value="new">New Regime (FY 2025-26)</option>
                <option value="old">Old Regime</option>
              </select>
            </div>
            {regime === 'old' && (
              <>
                <div className="form-group">
                  <label htmlFor="sal-hra">HRA Exemption (₹)</label>
                  <input id="sal-hra" type="number" min="0" value={hra} onChange={function (e) { setHra(parseFloat(e.target.value) || 0); }} className="color-input" />
                </div>
                <div className="form-group">
                  <label htmlFor="sal-80c">Section 80C (₹, max 1.5L)</label>
                  <input id="sal-80c" type="number" min="0" max="150000" value={section80c} onChange={function (e) { setSection80c(parseFloat(e.target.value) || 0); }} className="color-input" />
                </div>
                <div className="form-group">
                  <label htmlFor="sal-80d">Section 80D - Health Insurance (₹)</label>
                  <input id="sal-80d" type="number" min="0" max="100000" value={section80d} onChange={function (e) { setSection80d(parseFloat(e.target.value) || 0); }} className="color-input" />
                </div>
                <div className="form-group">
                  <label htmlFor="sal-epf">EPF Contribution (₹)</label>
                  <input id="sal-epf" type="number" min="0" value={epf} onChange={function (e) { setEpf(parseFloat(e.target.value) || 0); }} className="color-input" />
                </div>
                <div className="form-group">
                  <label htmlFor="sal-other">Other Deductions (₹)</label>
                  <input id="sal-other" type="number" min="0" value={otherDeductions} onChange={function (e) { setOtherDeductions(parseFloat(e.target.value) || 0); }} className="color-input" />
                </div>
              </>
            )}
          </div>
          <div className="calc-results">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', width: '100%' }}>
              <div className="stat-card"><div className="stat-number">{fmt(result.grossAnnual)}</div><div className="stat-label">Gross Annual</div></div>
              <div className="stat-card"><div className="stat-number">{fmt(result.grossMonthly)}</div><div className="stat-label">Gross Monthly</div></div>
              <div className="stat-card"><div className="stat-number" style={{ color: 'var(--error)' }}>{fmt(result.tax)}</div><div className="stat-label">Total Tax + Cess</div></div>
              <div className="stat-card"><div className="stat-number">{result.effectiveRate}%</div><div className="stat-label">Effective Tax Rate</div></div>
              <div className="stat-card"><div className="stat-number" style={{ color: 'var(--accent)' }}>{fmt(result.netAnnual)}</div><div className="stat-label">Net Annual</div></div>
              <div className="stat-card"><div className="stat-number" style={{ color: 'var(--accent)' }}>{fmt(result.netMonthly)}</div><div className="stat-label">Net Monthly</div></div>
            </div>
            <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border)' }}>
              <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Regime Comparison</h3>
              <div className="stats-grid" style={{ width: '100%' }}>
                <div className="stat-card"><div className="stat-number">{fmt(result.oldTax)}</div><div className="stat-label">Old Regime Tax</div></div>
                <div className="stat-card"><div className="stat-number">{fmt(result.newTax)}</div><div className="stat-label">New Regime Tax</div></div>
              </div>
              <p style={{ margin: '0.75rem 0 0', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                {result.oldTax < result.newTax ? '✅ Old regime saves you ' + fmt(result.newTax - result.oldTax) + ' per year.' : result.newTax < result.oldTax ? '✅ New regime saves you ' + fmt(result.oldTax - result.newTax) + ' per year.' : 'Both regimes result in the same tax.'}
              </p>
            </div>
          </div>
        </div>
        <section className="tool-guide">
          <h2>Indian Income Tax Slabs (FY 2025-26)</h2>
          <p>The new tax regime offers lower rates with fewer deductions. The old regime allows deductions under 80C, 80D, HRA, etc. Use the regime comparison above to find which works better for your income level.</p>
          <h2>New Regime Slabs</h2>
          <ul>
            <li>Up to ₹4,00,000 — Nil</li>
            <li>₹4,00,001 to ₹8,00,000 — 5%</li>
            <li>₹8,00,001 to ₹12,00,000 — 10%</li>
            <li>₹12,00,001 to ₹16,00,000 — 15%</li>
            <li>₹16,00,001 to ₹20,00,000 — 20%</li>
            <li>₹20,00,001 to ₹24,00,000 — 25%</li>
            <li>Above ₹24,00,000 — 30%</li>
          </ul>
          <h2>Tax Saving Tips</h2>
          <ul>
            <li>Maximize Section 80C investments (PPF, ELSS, EPF) up to ₹1.5 lakh under old regime.</li>
            <li>Claim HRA exemption if you pay rent and are salaried.</li>
            <li>Health insurance premiums qualify under Section 80D (₹25K self, ₹50K for senior citizen parents).</li>
            <li>NPS contributions get additional ₹50,000 deduction under Section 80CCD(1B) in old regime.</li>
            <li>Compare both regimes every year — the better option depends on your deductions.</li>
          </ul>
        </section>
      </div>
    </div>
  );
}

export default SalaryCalculator;
